// client/src/lib/axiosConfig.js

/**
 * Global axios configuration.
 *
 * Every portal (superadmin, vendor, school admin, teacher, student) now
 * authenticates with an HTTP-only session cookie, so every request has to send
 * credentials. Setting it once here covers all call sites instead of relying on
 * each one remembering `withCredentials: true` - which is how the admin portal
 * ended up with no session at all.
 */

import axios from 'axios';
import { beginActivity, endActivity } from './activity';

axios.defaults.withCredentials = true;

// Endpoints that answer 401 as a normal "you are not logged in" result.
// Redirecting on these would bounce a visitor off the login page they just opened.
const SESSION_PROBE_PATHS = ['/app/verify', '/vendor/verify', '/superadmin/verify'];

// Which login page a 401 should land on, based on the portal the user is in.
const LOGIN_ROUTES = [
    { prefix: '/superadmin', login: '/superadmin/login' },
    { prefix: '/vendor', login: '/vendor/login' },
];

const loginRouteFor = (pathname) => {
    const match = LOGIN_ROUTES.find(route => pathname.startsWith(route.prefix));
    return match ? match.login : '/login';
};

/**
 * On an expired or missing session, drop the persisted client state and send the
 * user to the right login page. Without this a stale localStorage snapshot keeps
 * rendering a dashboard whose every API call is failing.
 *
 * The redirect carries ?reason=expired so the login page can explain what
 * happened - being bounced to a login screen with no message reads like a bug.
 */
// Feeds the global progress bar / "saving" pill (see lib/activity.js).
// Requests can opt out with `{ silent: true }`.
axios.interceptors.request.use((config) => {
    if (!config.silent) {
        config.__tracked = true;
        beginActivity(config.method);
    }
    return config;
});

const finishTracking = (config) => {
    if (config?.__tracked) {
        config.__tracked = false;
        endActivity(config.method);
    }
};

axios.interceptors.response.use(
    (response) => {
        finishTracking(response.config);
        return response;
    },
    (error) => {
        finishTracking(error?.config);
        const status = error?.response?.status;
        const requestUrl = error?.config?.url || '';

        const isSessionProbe = SESSION_PROBE_PATHS.some(path => requestUrl.includes(path));

        if (status === 401 && !isSessionProbe && typeof window !== 'undefined') {
            const target = loginRouteFor(window.location.pathname);

            // Already on the login page - let the form show the error instead.
            if (window.location.pathname !== target) {
                const role = localStorage.getItem('userRole');

                localStorage.removeItem('state');
                localStorage.removeItem('userRole');

                const params = new URLSearchParams({ reason: 'expired' });
                // Reopen the tab they were using so they are not asked to find it again.
                if (role && target === '/login') params.set('role', role);

                window.location.href = `${target}?${params.toString()}`;
            }
        }

        return Promise.reject(error);
    }
);

export default axios;
